import React, {useCallback, useEffect, useState} from 'react';
import {App, Button, Card, Descriptions, Spin, Table, Tag} from 'antd';
import {useI18n} from '../../i18n/useI18n';
import {getMyMembership, MembershipSubscription, MembershipUserInfo,} from '../../services/membershipService';
import MembershipCompareTable from './MembershipCompareTable';

/** 等级徽标配色（0灰/1蓝/2紫/3金） */
const LEVEL_COLORS: Record<number, string> = {
    0: 'default',
    1: 'blue',
    2: 'purple',
    3: 'gold',
};

/**
 * 会员中心页
 * 路径：/membership（登录用户）
 * 当前等级/到期时间/DCA 上限 + 等级功能对比 + 订阅历史 + 续费引导（联系运营）
 *
 * @author page
 * @date 2026-10-15
 */
const MembershipPage: React.FC = () => {
    const {t} = useI18n();
    const {message} = App.useApp();
    const [info, setInfo] = useState<MembershipUserInfo | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setInfo(await getMyMembership());
        } catch (e: any) {
            message.error(e?.message ?? t('membership.load.error'));
        } finally {
            setLoading(false);
        }
    }, [message, t]);

    useEffect(() => {
        void load();
    }, [load]);

    const historyColumns = [
        {
            title: t('membership.center.level'),
            dataIndex: 'level',
            render: (level: number) => (
                <Tag color={LEVEL_COLORS[level] ?? 'default'}>{t(`membership.level.${level}`)}</Tag>
            ),
        },
        {title: t('membership.center.period'), dataIndex: 'period', render: (p: string) => t(`membership.period.${p}`)},
        {title: t('membership.center.amount'), dataIndex: 'amount'},
        {title: t('membership.center.startTime'), dataIndex: 'startTime'},
        {title: t('membership.center.expireTime'), dataIndex: 'expireTime'},
        {
            title: t('membership.center.openMethod'),
            dataIndex: 'openMethod',
            render: (m: string) => t(`membership.openMethod.${m}`)
        },
        {title: t('membership.center.remark'), dataIndex: 'remark', ellipsis: true},
    ];

    return (
        <div style={{padding: 24, maxWidth: 1000}}>
            <Card
                title={t('membership.center.title')}
                style={{marginBottom: 16}}
                extra={
                    <Button type="primary" onClick={() => window.open('/site/contact', '_blank')}>
                        {t('membership.center.contact')}
                    </Button>
                }
            >
                {loading ? (
                    <Spin/>
                ) : info ? (
                    <>
                        <Descriptions column={3}>
                            <Descriptions.Item label={t('membership.center.currentLevel')}>
                                <Tag color={LEVEL_COLORS[info.currentLevel] ?? 'default'}>
                                    {t(`membership.level.${info.currentLevel}`)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('membership.center.expireTime')}>
                                {info.expireTime ?? t('membership.center.noActive')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('membership.center.dcaPlanLimit')}>
                                {info.dcaPlanLimit}
                            </Descriptions.Item>
                        </Descriptions>
                        <div style={{marginTop: 8, color: 'var(--pt-text-secondary, #888)', fontSize: 13}}>
                            {t('membership.center.renewGuide')}
                        </div>
                    </>
                ) : (
                    <div>{t('membership.load.error')}</div>
                )}
            </Card>

            <Card title={t('membership.center.compare')} style={{marginBottom: 16}}>
                {info ? <MembershipCompareTable pricing={info.pricing}/> : <Spin/>}
            </Card>

            <Card title={t('membership.center.history')}>
                {info && 0 < info.history.length ? (
                    <Table<MembershipSubscription>
                        columns={historyColumns}
                        dataSource={info.history}
                        rowKey="id"
                        pagination={false}
                        size="small"
                    />
                ) : (
                    <div style={{color: 'var(--pt-text-dim, #999)'}}>
                        {loading ? '' : t('membership.center.history.empty')}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default MembershipPage;
